/** Identify paint-image positions from the PDF.js display operator list.
 * Boxes are obtained from each image paint's current transformation matrix,
 * not guessed from a screenshot or manually drawn rectangles.
 * This is a best-effort detector: masks, patterns, forms and clipped/rotated
 * graphics are not always independently editable PDF image objects. */
export function multiplyPdfTransform(a,b) {
  return [
    a[0]*b[0]+a[2]*b[1], a[1]*b[0]+a[3]*b[1],
    a[0]*b[2]+a[2]*b[3], a[1]*b[2]+a[3]*b[3],
    a[0]*b[4]+a[2]*b[5]+a[4], a[1]*b[4]+a[3]*b[5]+a[5],
  ];
}


export function matrixRect(matrix, bounds) {
  const pts = [[0,0],[1,0],[1,1],[0,1]].map(([x,y]) => ({
    x:matrix[0]*x+matrix[2]*y+matrix[4],
    y:matrix[1]*x+matrix[3]*y+matrix[5],
  }));
  const x=Math.max(0,Math.min(...pts.map(p=>p.x)));
  const y=Math.max(0,Math.min(...pts.map(p=>p.y)));
  const right=Math.min(bounds.width,Math.max(...pts.map(p=>p.x)));
  const bottom=Math.min(bounds.height,Math.max(...pts.map(p=>p.y)));
  if (!Number.isFinite(x+y+right+bottom)) return null;
  if(right-x < 5 || bottom-y < 5) return null;
  return {x,y,width:right-x,height:bottom-y};
}

export function imageSelectionRegions(operatorList, viewport, bounds, ops) {
  if(!operatorList?.fnArray || !ops) return [];
  let current=[1,0,0,1,0,0];
  const stack=[];
  const result=[];
  const images=new Set([ops.paintImageXObject,ops.paintInlineImageXObject,ops.paintJpegXObject]);
  const imageMasks=new Set([ops.paintImageMaskXObject,ops.paintImageMaskXObjectGroup,
    ops.paintImageMaskXObjectRepeat,ops.paintSolidColorImageMask].filter(Number.isFinite));
  for(let i=0;i<operatorList.fnArray.length;i++) {
    const op=operatorList.fnArray[i];
    const args=operatorList.argsArray[i] || [];
    if(op===ops.save) stack.push([...current]);
    else if(op===ops.restore) current=stack.pop() || [1,0,0,1,0,0];
    else if(op===ops.transform && args.length>=6) current=multiplyPdfTransform(current,args);
    else if(images.has(op) || imageMasks.has(op)) {
      const rect=matrixRect(multiplyPdfTransform(viewport.transform,current),bounds);
      if(rect) result.push({id:`image-${i}`, ...rect, operatorIndex:i, imageMask:imageMasks.has(op)});
    }
  }
  // Smaller nested images should be selectable before full-page backgrounds.
  return result.sort((a,b)=>a.width*a.height-b.width*b.height);
}

export function matchPdfFont(pdfFontName='',pdfStyleFamily='') {
  const name=`${pdfFontName} ${pdfStyleFamily}`.toLowerCase();
  const bold=/bold|black|heavy|demi|semibold|medium/.test(name);
  const italic=/italic|oblique/.test(name);
  let fontFamily='Noto Sans';
  if(/times|garamond|baskerville|georgia|cambria|serif/.test(name)) fontFamily='Noto Serif';
  if(/courier|mono|consolas|code/.test(name)) fontFamily='Source Code Pro';
  if(/arial|helvetica|liberation sans/.test(name)) fontFamily='Arial';
  if(/roboto/.test(name)) fontFamily='Roboto';
  if(/montserrat/.test(name)) fontFamily='Montserrat';
  if(/lato/.test(name)) fontFamily='Lato';
  return {fontFamily,bold,italic,pdfFontName,pdfStyleFamily};
}

export function hasEmbeddedChange(overlays=[]) {
  return overlays.some(o=>!o.hidden && !!o.originalRegion);
}
